import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Heart, MoreVertical, ArrowLeft, Music, User, Shuffle, UserPlus, UserCheck } from 'lucide-react';
import SongMenu from '../SongMenu';

const SharedPlaylist = ({ playSong, toggleLike, likedSongs = new Set(), user }) => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  // Helper function to check if song is liked (handles both Set and Array)
  const isLiked = (songId) => {
    if (likedSongs instanceof Set) {
      return likedSongs.has(songId);
    }
    return likedSongs.includes(songId);
  };

  useEffect(() => {
    fetchSharedPlaylist();
  }, [shareToken]);

  useEffect(() => {
    if (playlist?.creator_id && user) {
      checkFollowStatus();
    }
  }, [playlist, user]);

  const fetchSharedPlaylist = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/playlists/shared/${shareToken}`
      );
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Playlist not found or is no longer shared');
        } else {
          setError('Failed to load shared playlist');
        }
        return;
      }
      
      const data = await response.json();
      setPlaylist(data);
    } catch (error) {
      console.error('Failed to fetch shared playlist:', error);
      setError('Failed to load shared playlist');
    } finally {
      setLoading(false);
    }
  };

  const playPlaylist = () => {
    if (playlist?.songs?.length > 0) {
      playSong(playlist.songs[0], playlist.songs);
    }
  };

  const shufflePlaylist = () => {
    if (playlist?.songs?.length > 0) {
      const shuffled = [...playlist.songs].sort(() => Math.random() - 0.5);
      playSong(shuffled[0], shuffled);
    }
  };

  const checkFollowStatus = async () => {
    if (!user || !playlist?.creator_id) return;
    
    // Don't check if viewing own playlist
    if (user.id === playlist.creator_id) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${playlist.creator_id}/is-following`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setIsFollowing(data.is_following);
      }
    } catch (error) {
      console.error('Failed to check follow status:', error);
    }
  };

  const handleFollowToggle = async () => {
    if (!user) {
      alert('Please log in to follow users');
      navigate('/login');
      return;
    }

    if (user.id === playlist.creator_id) {
      return; // Can't follow yourself
    }

    setFollowLoading(true);
    try {
      const method = isFollowing ? 'DELETE' : 'POST';
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${playlist.creator_id}/follow`,
        {
          method,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        setIsFollowing(!isFollowing);
      } else {
        alert('Failed to update follow status');
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
      alert('Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '50vh'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Music size={48} color="#9D4EDD" style={{ marginBottom: '16px' }} />
          <p style={{ fontSize: '16px', color: '#999' }}>Loading playlist...</p>
        </div>
      </div>
    );
  }

  if (error || !playlist) {
    return (
      <div className="page-content">
        <div className="error-state" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Music size={64} color="#666" style={{ marginBottom: '24px' }} />
          <h2 style={{ marginBottom: '12px' }}>Playlist Not Found</h2>
          <p style={{ color: '#999', marginBottom: '24px' }}>
            {error || 'This playlist might be private or the link is invalid'}
          </p>
          <button 
            onClick={() => navigate('/')} 
            className="button primary"
            style={{ margin: '0 auto' }}
          >
            <ArrowLeft size={16} />
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(180deg, rgba(157, 78, 221, 0.2) 0%, transparent 100%)',
        padding: '40px 20px',
        marginBottom: '32px',
        borderRadius: '12px'
      }}>
        <button 
          className="back-button" 
          onClick={() => navigate('/')}
          style={{ marginBottom: '24px' }}
        >
          <ArrowLeft size={20} />
          Back
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
          {/* Playlist Icon */}
          <div style={{
            width: '160px',
            height: '160px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 8px 24px rgba(157, 78, 221, 0.4)'
          }}>
            <Music size={64} color="white" />
          </div>

          {/* Playlist Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontSize: '12px', 
              color: '#9D4EDD', 
              fontWeight: '600',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Shared Playlist
            </div>
            <h1 style={{ 
              fontSize: '48px', 
              fontWeight: 'bold', 
              marginBottom: '16px',
              lineHeight: '1.1',
              wordBreak: 'break-word'
            }}>
              {playlist.name}
            </h1>
            {playlist.description && (
              <p style={{ 
                fontSize: '16px', 
                color: '#ccc', 
                marginBottom: '16px',
                lineHeight: '1.5'
              }}>
                {playlist.description}
              </p>
            )}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              fontSize: '14px',
              color: '#999',
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <User size={16} />
                <span>{playlist.creator_name}</span>
                {user && playlist.creator_id && user.id !== playlist.creator_id && (
                  <button
                    onClick={handleFollowToggle}
                    disabled={followLoading}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      border: isFollowing ? '1px solid #9D4EDD' : '1px solid rgba(157, 78, 221, 0.5)',
                      background: isFollowing ? 'rgba(157, 78, 221, 0.2)' : 'rgba(157, 78, 221, 0.1)',
                      color: '#9D4EDD',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: followLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s',
                      opacity: followLoading ? 0.6 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!followLoading) {
                        e.currentTarget.style.background = 'rgba(157, 78, 221, 0.3)';
                      }
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = isFollowing ? 'rgba(157, 78, 221, 0.2)' : 'rgba(157, 78, 221, 0.1)';
                    }}
                  >
                    {isFollowing ? (
                      <>
                        <UserCheck size={12} />
                        Following
                      </>
                    ) : (
                      <>
                        <UserPlus size={12} />
                        Follow
                      </>
                    )}
                  </button>
                )}
              </div>
              <span>•</span>
              <span>{playlist.song_count} songs</span>
            </div>
          </div>
        </div>

        {/* Play & Shuffle Buttons */}
        <div style={{ marginTop: '32px', display: 'flex', gap: '12px' }}>
          <button
            onClick={playPlaylist}
            disabled={!playlist.songs || playlist.songs.length === 0}
            style={{
              padding: '14px 32px',
              borderRadius: '24px',
              border: 'none',
              background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
              color: 'white',
              fontSize: '16px',
              fontWeight: '600',
              cursor: playlist.songs?.length > 0 ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 4px 12px rgba(157, 78, 221, 0.4)',
              opacity: playlist.songs?.length > 0 ? 1 : 0.5
            }}
            onMouseOver={(e) => {
              if (playlist.songs?.length > 0) {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(157, 78, 221, 0.6)';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(157, 78, 221, 0.4)';
            }}
          >
            <Play size={20} fill="white" />
            Play Playlist
          </button>
          
          <button
            onClick={shufflePlaylist}
            disabled={!playlist.songs || playlist.songs.length === 0}
            style={{
              padding: '14px 32px',
              borderRadius: '24px',
              border: '2px solid rgba(157, 78, 221, 0.8)',
              background: 'rgba(157, 78, 221, 0.1)',
              color: '#9D4EDD',
              fontSize: '16px',
              fontWeight: '600',
              cursor: playlist.songs?.length > 0 ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              opacity: playlist.songs?.length > 0 ? 1 : 0.5
            }}
            onMouseOver={(e) => {
              if (playlist.songs?.length > 0) {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.background = 'rgba(157, 78, 221, 0.2)';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.background = 'rgba(157, 78, 221, 0.1)';
            }}
          >
            <Shuffle size={20} />
            Shuffle
          </button>
        </div>
      </div>

      {/* Songs List */}
      {playlist.songs && playlist.songs.length > 0 ? (
        <div className="songs-list" style={{ padding: '0 20px' }}>
          <h3 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            marginBottom: '16px',
            color: '#fff'
          }}>
            Songs
          </h3>
          <div className="songs-table">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #333' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#999', fontWeight: '500', fontSize: '14px', width: '50px' }}>#</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#999', fontWeight: '500', fontSize: '14px' }}>Title</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#999', fontWeight: '500', fontSize: '14px' }}>Album</th>
                  <th style={{ padding: '12px', textAlign: 'center', color: '#999', fontWeight: '500', fontSize: '14px', width: '100px' }}>Duration</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: '#999', fontWeight: '500', fontSize: '14px', width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {playlist.songs.map((song, index) => (
                  <tr 
                    key={song.id}
                    style={{ 
                      borderBottom: '1px solid #2a2a2a',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '12px', color: '#999', fontSize: '14px' }}>
                      {index + 1}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div 
                        onClick={() => playSong(song, playlist.songs)}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        <img
                          src={song.albumArt}
                          alt={song.title}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '4px',
                            objectFit: 'cover'
                          }}
                        />
                        <div>
                          <div style={{ fontSize: '15px', fontWeight: '500', color: '#fff', marginBottom: '2px' }}>
                            {song.title}
                          </div>
                          <div style={{ fontSize: '13px', color: '#999' }}>
                            {song.artist}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px', color: '#ccc', fontSize: '14px' }}>
                      {song.album}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                      {Math.floor(song.duration / 60)}:{String(song.duration % 60).padStart(2, '0')}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleLike(song.id);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.2s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                          onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                        >
                          <Heart
                            size={18}
                            fill={isLiked(song.id) ? '#9D4EDD' : 'none'}
                            color={isLiked(song.id) ? '#9D4EDD' : '#999'}
                          />
                        </button>
                        {user && (
                          <SongMenu song={song} user={user} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          color: '#999'
        }}>
          <Music size={48} color="#666" style={{ marginBottom: '16px' }} />
          <p>This playlist is empty</p>
        </div>
      )}
    </div>
  );
};

export default SharedPlaylist;
